import { Fragment, PropsWithChildren } from 'react';
import { Dialog, DialogBackdrop, Transition } from '@headlessui/react';
import { Stack } from './Stack';
import { CircleButton } from './CircleButton';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { twMerge } from 'tailwind-merge';

type Props = PropsWithChildren<{
  open: boolean;
  title: React.ReactNode;
  description?: React.ReactNode;
  closeButton?: boolean;
  onClose: () => void;
  className?: string;
  titleClassName?: string;
  overlayClose?: boolean;
}>;

export function Modal({
  open,
  description,
  title,
  onClose,
  closeButton = false,
  children,
  className = '',
  titleClassName = '',
  overlayClose = true,
}: Props): React.ReactElement {
  const classes = twMerge('mx-auto max-w-5xl bg-white p-6 text-center font-aribau', className);
  const titleClasses = twMerge('w-full', titleClassName);

  const handleCloseEvent = (value: boolean) => {
    if (overlayClose) {
      onClose();
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog onClose={handleCloseEvent} className="relative z-10">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <DialogBackdrop className="fixed inset-0 bg-black/50" aria-hidden />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={classes}>
                <Stack gap={6}>
                  <Stack direction="row" className="justify-between items-center">
                    <Dialog.Title className={titleClasses}>{title}</Dialog.Title>
                    {closeButton && (
                      <CircleButton as="button" onClick={onClose} className="text-zinc-500">
                        <XMarkIcon className="size-5" />
                      </CircleButton>
                    )}
                  </Stack>

                  {description && (
                    <Dialog.Description className="font-aribaubold mt-5 text-4xl">
                      {description}
                    </Dialog.Description>
                  )}

                  {children}
                </Stack>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
